import { useEffect, useState, useRef } from "react";
import { Bell, Check } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";

export default function NotificationsBell({ variant = "light" }) {
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const ref = useRef(null);

    const load = async () => {
        try {
            const { data } = await api.get("/me/notifications");
            setItems(data.items || []);
            setUnread(data.unread || 0);
        } catch (_e) {
            // ignore silently — 401 handled globally
        }
    };

    useEffect(() => {
        load();
        const id = setInterval(load, 15000);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        const onClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    const markRead = async (id) => {
        await api.post(`/me/notifications/${id}/read`);
        await load();
    };

    const markAll = async () => {
        await api.post("/me/notifications/read-all");
        await load();
    };

    return (
        <div className="relative" ref={ref} data-testid="notifications-bell">
            <button
                className={
                    "relative inline-flex items-center justify-center h-9 w-9 rounded-full transition-colors " +
                    (variant === "dark"
                        ? "text-white/80 hover:bg-white/10"
                        : "text-[#123B5D]/80 hover:bg-[#123B5D]/5")
                }
                onClick={() => setOpen((v) => !v)}
                aria-label="notifications"
                data-testid="notifications-toggle"
            >
                <Bell size={18} />
                {unread > 0 ? (
                    <span
                        className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-[#DC2626] text-white text-[10px] font-bold flex items-center justify-center"
                        data-testid="notifications-unread-badge"
                    >
                        {/* W2-UX-NEW-008 — surface "99+" when the backend
                            caps the count instead of implying an exact 100. */}
                        {unread >= 100 ? "99+" : unread}
                    </span>
                ) : null}
            </button>
            {open ? (
                <div
                    className="absolute right-0 mt-2 w-96 max-h-[70vh] overflow-y-auto ddi-card z-50"
                    data-testid="notifications-panel"
                >
                    <div className="p-4 flex items-center justify-between border-b border-[#123B5D]/10">
                        <div className="font-semibold text-[#123B5D]">
                            Notifications
                        </div>
                        {unread > 0 ? (
                            <button
                                onClick={markAll}
                                className="text-xs text-[#0F766E] hover:underline"
                                data-testid="notifications-mark-all"
                            >
                                Mark all read
                            </button>
                        ) : null}
                    </div>
                    {items.length === 0 ? (
                        <div className="p-6 text-sm text-[#17212B]/50 text-center">
                            You&apos;re all caught up.
                        </div>
                    ) : (
                        <ul className="divide-y divide-[#123B5D]/5">
                            {items.map((n) => (
                                <li
                                    key={n.id}
                                    className={
                                        "p-4 text-sm " +
                                        (!n.is_read
                                            ? "bg-[#0F766E]/5"
                                            : "")
                                    }
                                    data-testid={`notification-${n.kind}`}
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <div className="font-semibold text-[#123B5D] truncate">
                                                {n.title}
                                            </div>
                                            <div className="text-[#17212B]/70 mt-1">
                                                {n.body}
                                            </div>
                                            <div className="mt-2 flex items-center gap-3">
                                                {n.link ? (
                                                    <Link
                                                        to={n.link}
                                                        onClick={() => {
                                                            markRead(n.id);
                                                            setOpen(false);
                                                        }}
                                                        className="text-xs text-[#0F766E] font-semibold hover:underline"
                                                    >
                                                        Open →
                                                    </Link>
                                                ) : null}
                                                {!n.is_read ? (
                                                    <button
                                                        onClick={() => markRead(n.id)}
                                                        className="text-xs text-[#17212B]/60 hover:text-[#0F766E] inline-flex items-center gap-1"
                                                    >
                                                        <Check size={12} /> Mark read
                                                    </button>
                                                ) : null}
                                            </div>
                                        </div>
                                        <div className="text-[10px] text-[#17212B]/40 shrink-0">
                                            {new Date(n.created_at).toLocaleString()}
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            ) : null}
        </div>
    );
}
